import React from 'react';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import {
  ChallengeStatus,
  FactorType,
  FlowStatus,
  type PushNotificationMetadata,
} from '@/api/generated';
import { AlternativesCard } from '@/components/alternatives-card';
import { StatusContainer } from '@/components/ui/status-container';
import { useLanguage } from '@/contexts/language-context';
import { StatusVariant } from '@/enums/status-variant';
import { useMfaOrchestrator } from '@/hooks/use-mfa-orchestrator';
import type { FactorStep } from '@/hooks/use-mfa-orchestrator.types';
import { useSignOn } from '@/hooks/use-sign-on';
import { EmailOtpFactor } from './email-otp-factor/email-otp-factor';
import { ExternalSsoFactor } from './external-sso-factor/external-sso-factor';
import { EXTERNAL_SSO_REDIRECT_URL_METADATA_KEY } from './external-sso-factor/external-sso-popup';
import { PasswordFactor } from './password-factor';
import { PushNotificationPolling } from './push-notification-factor/push-notification-polling/push-notification-polling';
import { SmsOtpFactor } from './sms-otp-factor/sms-otp-factor';
import { TotpFactor } from './totp-factor/totp-factor';
import { WebAuthnFactor } from './webauthn-factor/webauthn-factor';
import { WhatsappOtpFactor } from './whatsapp-otp-factor/whatsapp-otp-factor';

interface FactorRouterProps {
  factorType: FactorType;
  initialStepData?: FactorStep;
  initialHasAlternative?: boolean;
  onComplete: (status?: FlowStatus) => void;
  username?: string;
  onBack?: () => void;
  isEscalationFlow?: boolean;
}

export const FactorRouter: React.FC<FactorRouterProps> = ({
  factorType,
  initialStepData,
  initialHasAlternative,
  onComplete,
  username,
  onBack,
  isEscalationFlow,
}) => {
  const { t } = useLanguage();
  const { endSignOn } = useSignOn();
  const { state, submitResponse, fetchAlternatives, switchToAlternative } =
    useMfaOrchestrator({
      initialStep: factorType,
      initialStepData,
      initialHasAlternative,
      onFlowComplete: () => {
        // Completed → no card follows; drop the cover before the redirect.
        endSignOn();
        onComplete();
      },
      onError: (error: Error) => {
        console.error('FactorRouter: MFA orchestrator error:', error);
      },
    });

  // If showing alternatives, render alternatives card
  if (state.showAlternatives) {
    return (
      <AlternativesCard
        alternatives={state.alternatives}
        onSelectAlternative={switchToAlternative}
        isLoading={state.isSubmitting}
        isEscalationFlow={isEscalationFlow}
      />
    );
  }

  // If flow is completed, don't render any factors
  if (state.status === FlowStatus.COMPLETED) {
    return null;
  }

  // Push has its own terminal Declined UI; let it keep rendering on rejection.
  const isPushDeclined =
    state.currentStep === FactorType.PUSH_NOTIFICATION &&
    state.challengeStatus === ChallengeStatus.REJECTED;

  if (state.status === FlowStatus.FAILED && !isPushDeclined) {
    return (
      <StatusContainer
        variant={StatusVariant.ERROR}
        title={t('mfa.error')}
        message={state.errorMessage || t('mfa.genericError')}
      />
    );
  }

  // Check if current step has alternatives (from response level, not current_step)
  const hasAlternatives = state.hasAlternative;
  const backHandler = state.submitCount === 0 && onBack ? onBack : undefined;
  const nextResendAt = state.currentStepData?.metadata?.next_resend_at as
    | string
    | undefined;

  switch (state.currentStep) {
    case FactorType.PUSH_NOTIFICATION:
      return (
        <PushNotificationPolling
          submitResponse={submitResponse}
          metadata={
            state.currentStepData?.metadata as
              | PushNotificationMetadata
              | undefined
          }
          hasAlternatives={hasAlternatives}
          onShowAlternatives={fetchAlternatives}
          username={username}
          onBack={backHandler}
          isEscalationFlow={isEscalationFlow}
        />
      );

    case FactorType.EXTERNAL_SSO:
      return (
        <ExternalSsoFactor
          submitResponse={submitResponse}
          redirectUrl={
            state.currentStepData?.metadata?.[
              EXTERNAL_SSO_REDIRECT_URL_METADATA_KEY
            ] as string
          }
          hasAlternatives={hasAlternatives}
          onShowAlternatives={fetchAlternatives}
          username={username}
          onBack={backHandler}
          isEscalationFlow={isEscalationFlow}
        />
      );

    case FactorType.PASSWORD:
      return (
        <PasswordFactor
          submitPassword={submitResponse}
          email={
            (state.currentStepData?.metadata?.email ??
              state.currentStepData?.metadata?.username ??
              state.currentStepData?.metadata?.userEmail) as string | undefined
          }
          hasAlternatives={hasAlternatives}
          onShowAlternatives={fetchAlternatives}
          username={username}
          onBack={backHandler}
          isEscalationFlow={isEscalationFlow}
        />
      );

    case FactorType.WHATSAPP_OTP:
      return (
        <WhatsappOtpFactor
          submitResponse={submitResponse}
          phoneNumberObfuscated={
            state.currentStepData?.metadata?.phone_number_obfuscated as
              | string
              | undefined
          }
          nextResendAt={nextResendAt}
          hasAlternatives={hasAlternatives}
          onShowAlternatives={fetchAlternatives}
          username={username}
          onBack={backHandler}
          isEscalationFlow={isEscalationFlow}
        />
      );

    case FactorType.SMS_OTP:
      return (
        <SmsOtpFactor
          submitResponse={submitResponse}
          phoneNumberObfuscated={
            state.currentStepData?.metadata?.phone_number_obfuscated as
              | string
              | undefined
          }
          nextResendAt={nextResendAt}
          hasAlternatives={hasAlternatives}
          onShowAlternatives={fetchAlternatives}
          username={username}
          onBack={backHandler}
          isEscalationFlow={isEscalationFlow}
        />
      );

    case FactorType.EMAIL_OTP:
      return (
        <EmailOtpFactor
          submitResponse={submitResponse}
          emailObfuscated={
            state.currentStepData?.metadata?.email_obfuscated as
              | string
              | undefined
          }
          nextResendAt={nextResendAt}
          hasAlternatives={hasAlternatives}
          onShowAlternatives={fetchAlternatives}
          username={username}
          onBack={backHandler}
          isEscalationFlow={isEscalationFlow}
        />
      );

    case FactorType.TOTP:
      return (
        <TotpFactor
          submitResponse={submitResponse}
          hasAlternatives={hasAlternatives}
          onShowAlternatives={fetchAlternatives}
          username={username}
          onBack={backHandler}
          isEscalationFlow={isEscalationFlow}
        />
      );

    case FactorType.WEBAUTHN:
      return (
        <WebAuthnFactor
          submitResponse={submitResponse}
          metadata={state.currentStepData?.metadata}
          hasAlternatives={hasAlternatives}
          onShowAlternatives={fetchAlternatives}
          username={username}
          onBack={backHandler}
          isEscalationFlow={isEscalationFlow}
        />
      );

    default:
      return (
        <div className='flex flex-col items-center justify-center min-h-96 space-y-6 p-8'>
          <Text
            as='p'
            size={TextSizes.XL}
            weight={FontWeights.BOLD}
            className='text-error-primary'
          >
            {t('mfa.unsupportedFactor')}
          </Text>
          <Text as='p' size={TextSizes.MD} className='text-center'>
            {t('mfa.unsupportedFactorMessage', {
              factorType: String(factorType),
            })}
          </Text>
        </div>
      );
  }
};
